import React, { useEffect, useState, useRef } from "react";
import {
  Card,
  Input,
  Button,
  List,
  message as antdMessage,
  Spin,
  Tag,
} from "antd";
import { SendOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../../axiosInstance";

export default function ConversationView() {
  const navigate = useNavigate();
  const { id } = useParams();
  const auth = JSON.parse(localStorage.getItem("auth_user") || "{}");
  const userId = auth?.id;
  const isAdmin = auth?.role?.toLowerCase() === "admin";

  const [loading, setLoading] = useState(false);
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState("");
  const chatEndRef = useRef(null);

  const fetchMessages = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const convRes = await axiosInstance.get(
        `/get_conversations.php?role=${
          isAdmin ? "ADMIN" : "EMPLOYEE"
        }&user_id=${userId}`
      );
      const foundConv = convRes.data.data?.find((c) => c.id === parseInt(id));
      setConversation(foundConv || null);

      const msgRes = await axiosInstance.get(
        `/get_messages.php?conversation_id=${id}`
      );
      if (msgRes.data.success) setMessages(msgRes.data.data || []);
      else antdMessage.error(msgRes.data.message || "Failed to load messages");
    } catch (err) {
      console.error(err);
      antdMessage.error("Error fetching conversation");
    } finally {
      setLoading(false);
      scrollToBottom();
    }
  };

  useEffect(() => {
    fetchMessages();
    const interval = setInterval(fetchMessages, 6000);
    return () => clearInterval(interval);
  }, [id]);

  const scrollToBottom = () => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const sendMessage = async () => {
    if (!newMsg.trim()) return;
    try {
      await axiosInstance.post("/send_message.php", {
        conversation_id: id,
        sender_id: userId,
        message: newMsg,
        is_admin: isAdmin ? 1 : 0,
      });
      setNewMsg("");
      fetchMessages();
    } catch (err) {
      console.error(err);
      antdMessage.error("Failed to send message");
    }
  };

  return (
    <div className="p-6 min-h-screen bg-gray-50">
      <Card
        bordered={false}
        className="rounded-xl shadow-sm"
        title={
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <Button
                icon={<ArrowLeftOutlined />}
                type="text"
                onClick={() => navigate("/support")}
              />
              <h3 className="m-0">
                {isAdmin
                  ? conversation?.employee_name || "Employee"
                  : conversation?.admin_name || "Admin"}
              </h3>
            </div>
            <Tag color="blue">{conversation?.status || "OPEN"}</Tag>
          </div>
        }
      >
        {loading ? (
          <div className="text-center py-20">
            <Spin size="large" />
          </div>
        ) : (
          <>
            <div
              className="max-h-[60vh] overflow-y-auto border rounded-lg p-4 bg-white"
              style={{ scrollBehavior: "smooth" }}
            >
              <List
                dataSource={messages}
                renderItem={(msg) => (
                  <List.Item
                    className={`border-0 ${
                      msg.is_admin
                        ? "justify-end text-right"
                        : "justify-start text-left"
                    }`}
                  >
                    <div
                      className={`p-3 rounded-lg inline-block ${
                        msg.is_admin
                          ? "bg-blue-100 text-blue-900"
                          : "bg-gray-100 text-gray-900"
                      } max-w-[70%]`}
                    >
                      <div className="text-sm">{msg.message}</div>
                      <div className="text-xs text-gray-500 mt-1">
                        {msg.created_at}
                      </div>
                    </div>
                  </List.Item>
                )}
              />
              <div ref={chatEndRef} />
            </div>

            <div className="flex gap-2 mt-4">
              <Input
                value={newMsg}
                onChange={(e) => setNewMsg(e.target.value)}
                placeholder="Type your message..."
                onPressEnter={sendMessage}
              />
              <Button
                type="primary"
                icon={<SendOutlined />}
                onClick={sendMessage}
              />
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
